import type {
	BiMetricConfig,
	BiMetricOut,
	BiMetricUpdateIn,
	BiMetricVersionOut,
	ChartColumnConfig,
} from '@repo/api-codegen';
import {
	apiGetBiMetricQueryKey,
	apiGetBiMetricVersionQueryKey,
	fetchApiGetBiMetricVersion,
	fetchApiRefreshBiMetricVersion,
	fetchApiResetSqlPlayground,
	fetchApiUpdateBiMetric,
} from '@repo/api-codegen';

import dayjs from 'dayjs';
import { cloneDeep, entries, isEqual, isNumber } from 'lodash-es';
import { makeAutoObservable, runInAction } from 'mobx';
import type { IUser, IWorkspace } from '../../api';
import { metricsQueryKeyFactory, queryClient } from '../../api';
import { fetchMetric } from '../../api/hooks/metricTerm/useGetOrCreateMetric';
import type { QueryResults } from '../../lib/types';
import { pollApiResult } from '../../utils/apiQueryUtils';
import {
	trackMetricChartTypeChanged,
	trackMetricSqlModified,
	trackMetricViewed,
} from '../BiDashboard/utils/tracking';

export type BIMetricMode = 'chart' | 'table' | 'sql';

// TODO(nhanthai-dashboards): Should already have utils for this
const tableToListDict = (input: QueryResults): Record<string, any>[] => {
	if (input.length === 0) {
		// return [];
	}

	const columns: string[] = input[0].map((column) => String(column));
	const result: Record<string, any>[] = [];

	for (let i = 1; i < input.length; i += 1) {
		const row = input[i];
		const rowDict: Record<string, any> = {};

		for (let j = 0; j < columns.length; j += 1) {
			rowDict[columns[j]] = row[j];
		}
		result.push(rowDict);
	}
	return result;
};

// TODO(nhanthai-dashboards): Write tests for this
const createDefaultTableChartConfig = (
	results: Record<string, any>[]
): BiMetricConfig['chart_config'] => {
	const columnConfigs: Record<string, ChartColumnConfig> = {};
	if (results.length > 0) {
		entries(results[0]).forEach(([column, value]) => {
			if (typeof value === 'string') {
				if (dayjs(value).isValid()) {
					columnConfigs[column] = {
						columnType: 'date',
					};
					return;
				}
			}

			if (isNumber(value)) {
				columnConfigs[column] = {
					columnType: 'number',
				};
				return;
			}

			columnConfigs[column] = {
				columnType: 'string',
			};
		});
	}

	return {
		chartType: 'table',
		columnConfigs,
	};
};

export class BIMetricStore {
	// Core data - source of truth
	metricId: string;
	latestServerMetricVersion: BiMetricVersionOut; // Original server data
	metricVersion: BiMetricVersionOut; // Editable working copy
	originalMetric: BiMetricOut; // Full original metric data including dashboards

	// UI state
	mode: BIMetricMode = 'chart';
	error: string | null = null;
	isRefreshing: boolean = false;
	isSavingChanges: boolean = false;
	lastResetPlaygroundAt: string;
	editable: boolean;

	// Analytics
	user?: IUser;
	workspace?: IWorkspace | null;
	isInDashboard: boolean;

	constructor({
		metric,
		initialMode,
		editable = true,
		user,
		workspace,
		isInDashboard = false,
	}: {
		metric: BiMetricOut;
		initialMode?: BIMetricMode;
		editable?: boolean;
		user?: IUser;
		workspace?: IWorkspace | null;
		isInDashboard?: boolean;
	}) {
		this.metricId = metric.id;
		this.originalMetric = metric;
		this.latestServerMetricVersion = metric.latest_version;
		// Create a deep copy for the editable version
		this.metricVersion = cloneDeep(metric.latest_version);

		this.mode = initialMode ?? 'chart';
		this.lastResetPlaygroundAt = new Date().toISOString();
		this.editable = editable;
		this.user = user;
		this.workspace = workspace;
		this.isInDashboard = isInDashboard;

		makeAutoObservable(this);

		this.pollSqlResults();
		this.resetSqlPlayground();
	}

	get hasChanges() {
		return (
			!isEqual(
				this.metricVersion.config,
				this.latestServerMetricVersion.config
			) ||
			this.metricVersion.sql !== this.latestServerMetricVersion.sql ||
			this.metricVersion.description !==
				this.latestServerMetricVersion.description ||
			this.metricVersion.name !== this.latestServerMetricVersion.name ||
			this.metricVersion.data_source_integration_id !==
				this.latestServerMetricVersion.data_source_integration_id
		);
	}

	get isChartMode() {
		return this.mode === 'chart';
	}

	get isTableMode() {
		return this.mode === 'table';
	}

	get isSqlMode() {
		return this.mode === 'sql';
	}

	get isUpdating() {
		return this.isSavingChanges;
	}

	// Actions
	setMode = (mode: BIMetricMode) => {
		this.mode = mode;
	};

	setError = (error: string | null) => {
		this.error = error;
	};

	// Analytics methods
	trackMetricViewed = () => {
		// Only track metric viewed when NOT in dashboard context
		if (!this.isInDashboard && this.user && this.workspace) {
			trackMetricViewed(this.metricId, this.user, this.workspace);
		}
	};

	// Direct setters for currentMetricVersion properties
	setDescription = (description: string) => {
		this.metricVersion.description = description;
	};

	setName = (name: string) => {
		this.metricVersion.name = name;
	};

	setChartConfig = (chartConfig: BiMetricConfig['chart_config']) => {
		this.metricVersion.config.chart_config = chartConfig;
	};

	setSql = (sql: string) => {
		this.metricVersion.sql = sql;
	};

	syncWithLatestUpdate = (metric: BiMetricOut) => {
		// TODO(nhanthai-dashboards): Improve this, what happened when we still have uncommitted changes?
		this.originalMetric = metric;
		this.latestServerMetricVersion = metric.latest_version;
		this.metricVersion = cloneDeep(metric.latest_version);
		this.pollSqlResults();
		this.resetSqlPlayground();
	};

	resetSqlPlayground = async () => {
		if (!this.editable) {
			return;
		}
		await fetchApiResetSqlPlayground({
			pathParams: { biMetricVersionId: this.metricVersion.id },
		});
		await queryClient.refetchQueries({
			queryKey: metricsQueryKeyFactory.byId(
				this.metricVersion.sql_playground_id
			),
		});
		await queryClient.refetchQueries({
			queryKey: [
				...metricsQueryKeyFactory.all(),
				'get_or_create',
				this.metricVersion.sql_playground_id,
			],
		});

		runInAction(() => {
			this.lastResetPlaygroundAt = new Date().toISOString();
		});
	};

	refresh = async () => {
		this.isRefreshing = true;

		try {
			await fetchApiRefreshBiMetricVersion({
				pathParams: { biMetricVersionId: this.metricVersion.id },
			});
			await this.pollSqlResults();
		} finally {
			this.isRefreshing = false;
		}
	};

	pollSqlResults = async () => {
		const biMetricVersion = await pollApiResult({
			queryKey: apiGetBiMetricVersionQueryKey({
				pathParams: { biMetricVersionId: this.metricVersion.id },
			}),
			queryFn: () =>
				fetchApiGetBiMetricVersion({
					pathParams: { biMetricVersionId: this.metricVersion.id },
				}),
			pollingInterval: (data) => {
				if (
					data.results_fetching_status === 'completed' ||
					data.results_fetching_status === 'failed'
				) {
					return null;
				}
				return 2000;
			},
		});

		runInAction(() => {
			this.metricVersion.results = biMetricVersion.results;
			this.metricVersion.results_fetching_status =
				biMetricVersion.results_fetching_status;

			this.latestServerMetricVersion = biMetricVersion;
		});
	};

	onNewSqlExecution = async (executedSqlPlaygroundId: string) => {
		const newSqlPlaygroundResult = await fetchMetric(executedSqlPlaygroundId);

		runInAction(() => {
			this.metricVersion.sql = newSqlPlaygroundResult.sql;
			this.metricVersion.data_source_integration_id =
				newSqlPlaygroundResult.integration;
			this.metricVersion.results = tableToListDict(
				newSqlPlaygroundResult.results
			);
			this.metricVersion.config = {
				chart_config: createDefaultTableChartConfig(this.metricVersion.results),
			};
		});
	};

	// Save all changes to server
	saveChanges = async (
		{ isDraft }: { isDraft: boolean | null } = { isDraft: null }
	) => {
		const sqlChanged =
			this.metricVersion.sql !== this.latestServerMetricVersion.sql;
		const chartTypeChanged =
			this.metricVersion.config.chart_config.chartType !==
			this.latestServerMetricVersion.config.chart_config.chartType;

		const changes: BiMetricUpdateIn = {
			description: this.metricVersion.description,
			name: this.metricVersion.name,
			config: this.metricVersion.config,
			sql: this.metricVersion.sql,
			data_source_integration_id: this.metricVersion.data_source_integration_id,
		};

		if (isDraft !== null) {
			changes.is_draft = isDraft;
		}

		try {
			this.isSavingChanges = true;
			const updatedMetric = await fetchApiUpdateBiMetric({
				pathParams: { biMetricId: this.metricId },
				body: changes,
			});

			// Update server version to match current version
			this.latestServerMetricVersion = updatedMetric.latest_version;
			this.metricVersion = updatedMetric.latest_version;

			queryClient.invalidateQueries({
				queryKey: apiGetBiMetricQueryKey({
					pathParams: { biMetricId: this.metricId },
				}),
			});

			// Track analytics events after successful save
			if (this.user && this.workspace) {
				if (sqlChanged) {
					trackMetricSqlModified(this.metricId, this.user, this.workspace);
				}

				if (chartTypeChanged) {
					trackMetricChartTypeChanged(
						this.metricId,
						this.metricVersion.config.chart_config.chartType,
						this.user,
						this.workspace
					);
				}
			}
		} finally {
			this.isSavingChanges = false;
		}

		if (sqlChanged) {
			this.pollSqlResults();
		}
	};

	// Discard all changes and revert to server version
	discardChanges = () => {
		this.metricVersion = cloneDeep(this.latestServerMetricVersion);
		this.resetSqlPlayground();
	};
}
